import { Card } from "./ui/card";

interface GoogleMapProps {
  center?: {
    lat: number;
    lng: number;
  };
  zoom?: number;
  markers?: Array<{
    lat: number;
    lng: number;
    title?: string;
  }>;
  height?: string;
  className?: string;
  embedUrl?: string;
  showServiceAreas?: boolean;
}

// Google Maps API key from environment variable
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Denver metro service area neighborhoods with coordinates
const serviceAreas = [
  // Central Denver
  { name: "Capitol Hill", lat: 39.7312, lng: -104.9826 },
  { name: "Cherry Creek", lat: 39.7178, lng: -104.9536 },
  { name: "RiNo", lat: 39.7654, lng: -104.9812 },
  { name: "Highlands", lat: 39.7627, lng: -105.0136 },
  { name: "Wash Park", lat: 39.6978, lng: -104.9742 },
  { name: "Central Park", lat: 39.7589, lng: -104.8958 },
  { name: "Denver Downtown", lat: 39.7392, lng: -104.9903 },
  // South Denver
  { name: "Highlands Ranch", lat: 39.5536, lng: -104.9689 },
  { name: "Centennial", lat: 39.5807, lng: -104.8772 },
  { name: "Greenwood Village", lat: 39.6172, lng: -104.9508 },
  { name: "Cherry Hills", lat: 39.6414, lng: -104.9586 },
  { name: "Littleton", lat: 39.6133, lng: -105.0166 },
  { name: "Englewood", lat: 39.6478, lng: -104.9878 },
  { name: "Ken-Caryl", lat: 39.5764, lng: -105.1108 },
  { name: "Columbine", lat: 39.5875, lng: -105.0694 },
  { name: "DTC", lat: 39.5978, lng: -104.8917 },
  // West Denver
  { name: "Lakewood", lat: 39.7047, lng: -105.0814 },
  { name: "Golden", lat: 39.7555, lng: -105.2211 },
  { name: "Arvada", lat: 39.8028, lng: -105.0875 },
  { name: "Wheat Ridge", lat: 39.7661, lng: -105.0772 },
  { name: "Edgewater", lat: 39.7539, lng: -105.0625 },
  // East Denver
  { name: "Aurora", lat: 39.7294, lng: -104.8319 },
  { name: "Glendale", lat: 39.7056, lng: -104.9342 },
  { name: "Commerce City", lat: 39.8083, lng: -104.9339 },
];

const GoogleMap = (_props: GoogleMapProps) => {
  // Map temporarily removed from the site.
  return null;
};

// Legacy render kept below for reference (intentionally unreachable).
const _LegacyGoogleMap = ({
  center,
  zoom = 11,
  height = "400px",
  className = "",
  embedUrl,
  showServiceAreas = false,
}: GoogleMapProps) => {
  const defaultCenter = { lat: 39.6539, lng: -105.0447 };
  const mapCenter = center || defaultCenter;
  
  // Create a URL that shows the Denver metro area with service locations
  // Using "view" mode to show a broader area
  let mapUrl: string;
  
  if (embedUrl) {
    mapUrl = embedUrl;
  } else if (!GOOGLE_MAPS_API_KEY) {
    // Fallback to OpenStreetMap embed if no API key configured
    mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${mapCenter.lng - 0.5}%2C${mapCenter.lat - 0.3}%2C${mapCenter.lng + 0.5}%2C${mapCenter.lat + 0.3}&layer=mapnik&marker=${mapCenter.lat}%2C${mapCenter.lng}`;
  } else if (showServiceAreas) {
    // Create a custom My Maps embed or use directions mode to show coverage
    // For now, use a view centered on Denver showing the metro area
    mapUrl = `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_MAPS_API_KEY}&center=${mapCenter.lat},${mapCenter.lng}&zoom=${zoom}&maptype=roadmap`;
  } else {
    mapUrl = `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${mapCenter.lat},${mapCenter.lng}&zoom=${zoom}`;
  }

  return (
    <Card className={`overflow-hidden ${className}`}>
      <div style={{ height, width: "100%" }} className="relative">
        <iframe
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          src={mapUrl}
          title="Groomie Roomie service area map - Denver mobile pet grooming covering 25+ neighborhoods"
        />
        
        {/* Service Areas Legend Overlay */}
        {showServiceAreas && (
          <div className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-64 bg-background/95 backdrop-blur-sm rounded-lg shadow-lg p-3 max-h-48 overflow-y-auto">
            <h4 className="font-semibold text-sm mb-2 text-foreground">Service Areas</h4>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {serviceAreas.map((area) => (
                <div key={area.name} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                  <span className="text-muted-foreground truncate">{area.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default GoogleMap;
